import {
  convertToDate,
  convertToTime,
} from "../../../backend/timeFunctions";

/**
 * Email HTML Template Generator
 * Centralized location for all email HTML templates
 */

// Base email wrapper with common styling
export const createEmailWrapper = (content, title = "") => {
  return `
    <!DOCTYPE html>
    <html dir="rtl" lang="he">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${title}</title>
      <link href="https://fonts.googleapis.com/css2?family=Assistant:wght@200;300;400;500;600;700;800&display=swap" rel="stylesheet">
      <style>
        body {
          font-family: 'Assistant', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          line-height: 1.6;
          color: #333;
          margin: 0;
          padding: 0;
          background-color: #f4f4f4;
          direction: rtl;
          text-align: right;
        }
        .email-container {
          max-width: 600px;
          margin: 0 auto;
          background-color: #ffffff;
          box-shadow: 0 0 10px rgba(0,0,0,0.1);
          direction: rtl;
          text-align: right;
        }
        .email-header {
          background: linear-gradient(135deg, #39b54a 0%, #2d8e3a 100%);
          color: white;
          padding: 30px 20px;
          text-align: center;
          position: relative;
        }
        .email-header h1 {
          margin: 0;
          font-size: 32px;
          font-weight: 700;
          font-family: 'Assistant', sans-serif;
        }
        .email-body {
          padding: 30px 20px;
          font-family: 'Assistant', sans-serif;
          direction: rtl;
          text-align: right;
        }
        .event-details {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 20px;
          margin: 20px 0;
          border-right: 4px solid #39b54a;
          font-family: 'Assistant', sans-serif;
          direction: rtl;
          text-align: right;
        }
        .event-details h3 {
          margin-top: 0;
          color: #333;
          font-size: 18px;
          font-family: 'Assistant', sans-serif;
        }
        .event-details p {
          margin: 8px 0;
          font-size: 14px;
          font-family: 'Assistant', sans-serif;
        }
        .event-details strong {
          color: #39b54a;
          font-weight: 600;
          font-family: 'Assistant', sans-serif;
        }
        .payment-button {
          display: inline-block;
          background: #39b54a;
          color: white;
          padding: 12px 24px;
          text-decoration: none;
          border-radius: 6px;
          font-weight: 600;
          margin: 10px 0;
          transition: background-color 0.3s;
          font-family: 'Assistant', sans-serif;
        }
        .payment-button:hover {
          background: #2d8e3a;
        }
        .email-footer {
          background: #f8f9fa;
          padding: 20px;
          text-align: center;
          border-top: 1px solid #e9ecef;
          font-family: 'Assistant', sans-serif;
          direction: rtl;
        }
        .email-footer p {
          margin: 0;
          color: #666;
          font-size: 14px;
          font-family: 'Assistant', sans-serif;
        }
        @media only screen and (max-width: 600px) {
          .email-container {
            margin: 0;
            box-shadow: none;
          }
          .email-header, .email-body, .email-footer {
            padding: 20px 15px;
          }
        }
      </style>
    </head>
    <body>
      <div class="email-container">
        ${content}
      </div>
    </body>
    </html>
  `;
};

/**
 * Event Invitation Email Body
 * @param {Object} event - Event object with all details
 * @param {Object} options - Additional options for customization
 * @returns {string} HTML email body content (without wrapper)
 */
export const generateEventInvitationBody = (event, options = {}) => {
  const {
    includePaymentButton = false,
    customMessage = "",
    userOrganizationName = "SimplyCoach"
  } = options;

  // Format event details
  const eventDate = event.date ? convertToDate(event.date.seconds, event.date.nanoseconds) : "לא נקבע";
  const eventTime = event.date ? convertToTime(event.date.seconds, event.date.nanoseconds) : "לא נקבע";

  let endDate = null;
  let endTime = null;

  if (event.endDate) {
    endDate = convertToDate(event.endDate.seconds, event.endDate.nanoseconds);
    endTime = convertToTime(event.endDate.seconds, event.endDate.nanoseconds);
  }

  // Check if event spans multiple days
  const isMultiDayEvent = endDate && eventDate !== endDate;

  // Generate event details HTML based on whether it's a multi-day event
  let eventDetailsHTML;

  if (isMultiDayEvent) {
    // Multi-day event - show start and end date/time
    eventDetailsHTML = `
      <div class="event-details">
        <h3>${event.name}</h3>
        <p><strong>תאריך ושעת התחלה:</strong> ${eventDate} ${eventTime}</p>
        <p><strong>תאריך ושעת סיום:</strong> ${endDate} ${endTime}</p>
        ${event.location ? `<p><strong>מיקום:</strong> ${event.location}</p>` : ''}
      </div>
      ${event.details ? `
        <div class="event-details" style="margin-top: 15px;">
          <h3>פרטים נוספים</h3>
          <p>${event.details}</p>
        </div>
      ` : ''}
    `;
  } else {
    // Single day event - show date and start/end times
    eventDetailsHTML = `
      <div class="event-details">
        <h3>${event.name}</h3>
        <p><strong>תאריך:</strong> ${eventDate}</p>
        <p><strong>שעת התחלה:</strong> ${eventTime}</p>
        ${endTime ? `<p><strong>שעת סיום:</strong> ${endTime}</p>` : ''}
        ${event.location ? `<p><strong>מיקום:</strong> ${event.location}</p>` : ''}
      </div>
      ${event.details ? `
        <div class="event-details" style="margin-top: 15px;">
          <h3>פרטים נוספים</h3>
          <p>${event.details}</p>
        </div>
      ` : ''}
    `;
  }

  // Generate payment button HTML
  const paymentButtonHTML = includePaymentButton && event.paymentUrl ? `
    <div style="text-align: center; margin: 20px 0;">
      <a href="${event.paymentUrl}" class="payment-button" target="_blank">
        דף התשלום לאירוע
      </a>
    </div>
  ` : '';

  // Generate custom message HTML
  const customMessageHTML = customMessage ? `
    <div style="background: #e3f2fd; padding: 15px; border-radius: 6px; margin: 20px 0; border-right: 3px solid #39b54a;">
      <p style="margin: 0; font-style: italic;">${customMessage}</p>
    </div>
  ` : '';

  // Return email body content
  return `
    <div class="email-header">
      <h1>הזמנה ל${event.name}</h1>
    </div>
    
         <div class="email-body">
       <p>שלום!</p>
       <p>הינך מוזמן ל${event.name}!</p>
      
      ${eventDetailsHTML}
      
      ${customMessageHTML}
      
      ${paymentButtonHTML}
      
             <p>אנחנו מצפים לראותך!</p>
       
       <p>בברכה,<br><strong>צוות ${userOrganizationName}</strong></p>
     </div>
     
     <div class="email-footer">
       <p>נשלח מ-SimplyCoach</p>
       <p>${new Date().getFullYear()} SimplyCoach. כל הזכויות שמורות. © </p>
     </div>
  `;
};
